import { useState } from 'react';
import type {
  TrendAndNode,
  TrendQueryDefinition,
  TrendQueryNode,
  TrendPhraseNode,
  TrendSemanticNode
} from '../types/trendQuery';

export type TrendLeafType = 'phrase' | 'semantic';

type SimpleRow = {
  id: string;
  text: string;
  type: TrendLeafType;
};

type SimpleState = {
  leaf: SimpleRow;
};

interface TrendQueryBuilderProps {
  value: TrendQueryDefinition;
  onChange: (definition: TrendQueryDefinition) => void;
}

const createId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const createLeaf = (type: TrendLeafType = 'phrase', text = ''): TrendPhraseNode | TrendSemanticNode => ({
  id: createId(type),
  type,
  text
});

const createDefinition = (root: TrendQueryNode): TrendQueryDefinition => ({ version: 1, root });

const isLeaf = (node: TrendQueryNode): node is TrendPhraseNode | TrendSemanticNode =>
  node.type === 'phrase' || node.type === 'semantic';

const isSimpleExpression = (node: TrendQueryNode): node is TrendPhraseNode | TrendSemanticNode => isLeaf(node);

const toSimpleState = (definition: TrendQueryDefinition): SimpleState => {
  if (!isSimpleExpression(definition.root)) {
    throw new Error('Advanced trend query cannot be represented in simple mode.');
  }

  return {
    leaf: { id: definition.root.id, text: definition.root.text, type: definition.root.type }
  };
};

const fromSimpleState = (state: SimpleState): TrendQueryDefinition => createDefinition({
  id: state.leaf.id,
  type: state.leaf.type,
  text: state.leaf.text.trim()
} as TrendPhraseNode | TrendSemanticNode);

export const formatTrendQuery = (node: TrendQueryNode): string => {
  if (node.type === 'phrase') {
    const text = node.text.trim();
    return text ? (/\s/.test(text) ? `"${text}"` : text) : '""';
  }
  if (node.type === 'semantic') return node.text.trim() || '…';
  if (node.type === 'not') {
    const child = node.child.type === 'phrase' || node.child.type === 'semantic'
      ? formatTrendQuery(node.child)
      : `(${formatTrendQuery(node.child)})`;
    return `NOT ${child}`;
  }

  const joiner = node.type === 'and' ? ' AND ' : ' OR ';
  const content = node.children.map(child => {
    const formatted = formatTrendQuery(child);
    return child.type === node.type || child.type === 'phrase' || child.type === 'semantic' || child.type === 'not'
      ? formatted
      : `(${formatted})`;
  }).join(joiner);
  return content;
};

const hasPositiveBranch = (node: TrendQueryNode): boolean => {
  if (isLeaf(node)) return Boolean(node.text.trim());
  if (node.type === 'not') return false;
  return node.children.some(hasPositiveBranch);
};

export const validateTrendQuery = (definition: TrendQueryDefinition): string | null => {
  if (!definition.root || !hasPositiveBranch(definition.root)) {
    return 'Query must include a positive phrase or semantic term.';
  }

  return null;
};

const TypeToggle = ({
  value,
  onChange,
  ariaLabel
}: {
  value: TrendLeafType;
  onChange: (type: TrendLeafType) => void;
  ariaLabel: string;
}) => (
  <div data-component="TrendQueryTypeToggle" data-flow="term-type" className="inline-flex shrink-0 rounded-md border border-gray-300 bg-gray-50 p-0.5" role="group" aria-label={ariaLabel}>
    <button
      type="button"
      onClick={() => onChange('phrase')}
      aria-pressed={value === 'phrase'}
      className={`rounded px-2 py-1 text-xs font-medium transition-colors ${value === 'phrase' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
    >
      Phrase
    </button>
    <button
      type="button"
      onClick={() => onChange('semantic')}
      aria-pressed={value === 'semantic'}
      className={`rounded px-2 py-1 text-xs font-medium transition-colors ${value === 'semantic' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
    >
      Semantic
    </button>
  </div>
);

const LeafEditor = ({
  node,
  onChange,
  onRemove,
  onWrapNot,
  isRoot
}: {
  node: TrendPhraseNode | TrendSemanticNode;
  onChange: (node: TrendQueryNode) => void;
  onRemove?: () => void;
  onWrapNot?: () => void;
  isRoot: boolean;
}) => (
  <div data-component="TrendQueryLeafEditor" data-flow="advanced-leaf" className="flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-white p-2">
    <TypeToggle
      value={node.type}
      onChange={type => onChange({ ...node, type })}
      ariaLabel="Term type"
    />
    <input
      value={node.text}
      onChange={event => onChange({ ...node, text: event.target.value })}
      aria-label={`${node.type === 'phrase' ? 'Phrase' : 'Semantic'} text`}
      placeholder={node.type === 'phrase' ? 'Exact phrase' : 'Meaning or concept'}
      className="min-w-[12rem] flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
    />
    {!isRoot && onWrapNot && (
      <button type="button" onClick={onWrapNot} className="text-xs font-medium text-amber-700 hover:text-amber-900" aria-label="Wrap term with NOT">NOT</button>
    )}
    {!isRoot && onRemove && (
      <button type="button" onClick={onRemove} className="text-xs text-red-600 hover:text-red-800" aria-label="Remove term">Remove</button>
    )}
  </div>
);

const NodeEditor = ({
  node,
  onChange,
  onRemove,
  isRoot = false
}: {
  node: TrendQueryNode;
  onChange: (node: TrendQueryNode) => void;
  onRemove?: () => void;
  isRoot?: boolean;
}) => {
  if (node.type === 'phrase' || node.type === 'semantic') {
    return (
      <LeafEditor
        node={node}
        onChange={onChange}
        onRemove={onRemove}
        onWrapNot={!isRoot ? () => onChange({ id: createId('not'), type: 'not', child: node }) : undefined}
        isRoot={isRoot}
      />
    );
  }

  if (node.type === 'not') {
    return (
      <div data-component="TrendQueryNotEditor" data-flow="advanced-not" className="rounded border border-amber-200 bg-amber-50 p-2">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-amber-800">NOT</span>
          {!isRoot && onRemove && <button type="button" onClick={onRemove} className="text-xs text-red-600 hover:text-red-800" aria-label="Remove NOT wrapper">Remove</button>}
        </div>
        <NodeEditor node={node.child} onChange={child => onChange({ ...node, child })} isRoot={false} />
      </div>
    );
  }

  const addChild = () => onChange({
    ...node,
    children: [...node.children, createLeaf()]
  });

  const addGroup = () => onChange({
    ...node,
    children: [...node.children, {
      id: createId('and'),
      type: 'and',
      children: [createLeaf(), createLeaf()]
    } as TrendAndNode]
  });

  const updateChild = (index: number, child: TrendQueryNode) => {
    const children = node.children.slice();
    children[index] = child;
    onChange({ ...node, children });
  };

  const removeChild = (index: number) => {
    const children = node.children.filter((_, childIndex) => childIndex !== index);
    if (children.length === 1 && !(isRoot && children[0].type === 'not')) {
      onChange(children[0]);
    } else if (children.length === 0 && onRemove) {
      onRemove();
    } else {
      onChange({ ...node, children });
    }
  };

  return (
    <div data-component="TrendQueryGroupEditor" data-flow="advanced-group" className="rounded border border-gray-300 bg-gray-50 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
          <span>Match</span>
          <select
            value={node.type}
            onChange={event => onChange({ ...node, type: event.target.value as 'and' | 'or' })}
            aria-label="Group operator"
            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
          >
            <option value="and">ALL (AND)</option>
            <option value="or">ANY (OR)</option>
          </select>
        </label>
        <div className="flex items-center gap-2">
          {!isRoot && (
            <>
              <button type="button" onClick={() => onChange({ id: createId('not'), type: 'not', child: node })} className="text-xs font-medium text-amber-700 hover:text-amber-900" aria-label="Wrap group with NOT">NOT</button>
              {onRemove && <button type="button" onClick={onRemove} className="text-xs text-red-600 hover:text-red-800" aria-label="Remove group">Remove</button>}
            </>
          )}
        </div>
      </div>
      <div data-flow="advanced-children" className="space-y-2">
        {node.children.map((child, index) => (
          <NodeEditor
            key={child.id}
            node={child}
            onChange={next => updateChild(index, next)}
            onRemove={() => removeChild(index)}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-3">
        <button type="button" onClick={addChild} className="text-xs font-medium text-blue-600 hover:text-blue-800" aria-label="Add condition to group">+ Add condition</button>
        <button type="button" onClick={addGroup} className="text-xs font-medium text-blue-600 hover:text-blue-800" aria-label="Add nested group">+ Add group</button>
      </div>
      {node.children.length === 0 && <p className="mt-2 text-xs text-red-600">Add at least one condition.</p>}
    </div>
  );
};

const SimpleLeafEditor = ({ leaf, onChange }: { leaf: SimpleRow; onChange: (leaf: SimpleRow) => void }) => (
  <div data-component="TrendQuerySimpleLeaf" data-flow="simple-leaf" className="flex flex-wrap items-center gap-2">
    <TypeToggle
      value={leaf.type}
      onChange={type => onChange({ ...leaf, type })}
      ariaLabel="Simple query term type"
    />
    <input
      value={leaf.text}
      onChange={event => onChange({ ...leaf, text: event.target.value })}
      aria-label={`${leaf.type === 'phrase' ? 'Phrase' : 'Semantic'} text`}
      placeholder={leaf.type === 'phrase' ? 'Exact phrase' : 'Meaning or concept'}
      className="min-w-0 flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
    />
  </div>
);

export const TrendQueryBuilder = ({ value, onChange }: TrendQueryBuilderProps) => {
  const [mode, setMode] = useState<'simple' | 'advanced'>(() => isSimpleExpression(value.root) ? 'simple' : 'advanced');
  const [simple, setSimple] = useState<SimpleState>(() => (
    isSimpleExpression(value.root) ? toSimpleState(value) : { leaf: createLeaf() }
  ));
  const [modeSwitchMessage, setModeSwitchMessage] = useState<string | null>(null);

  const updateSimple = (next: SimpleState) => {
    setSimple(next);
    onChange(fromSimpleState(next));
  };

  const switchMode = (nextMode: 'simple' | 'advanced') => {
    if (nextMode === 'simple') {
      if (!isSimpleExpression(value.root)) {
        setModeSwitchMessage('Simple mode supports exactly one positive Phrase or Semantic term. Remove groups, additional conditions, or NOT exclusions in Advanced mode before switching.');
        return;
      }
      setSimple(toSimpleState(value));
    }

    setModeSwitchMessage(null);
    setMode(nextMode);
  };

  const updateAdvanced = (definition: TrendQueryDefinition) => {
    setModeSwitchMessage(null);
    onChange(definition);
  };

  const addAdvancedCondition = () => {
    if (isSimpleExpression(value.root)) {
      updateAdvanced(createDefinition({ id: createId('and'), type: 'and', children: [value.root, createLeaf()] }));
    }
  };

  const validationMessage = validateTrendQuery(value);
  const preview = formatTrendQuery(value.root);

  return (
    <div data-component="TrendQueryBuilder" className="space-y-4">
      <div data-flow="query-mode" className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Query conditions</h3>
          <p className="text-xs text-gray-500">Use Phrase for exact wording and Semantic for concepts.</p>
        </div>
        <div className="flex rounded-md border border-gray-300 bg-gray-50 p-0.5" role="group" aria-label="Query builder mode">
          <button type="button" onClick={() => switchMode('simple')} aria-pressed={mode === 'simple'} className={`rounded px-3 py-1.5 text-xs font-medium ${mode === 'simple' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}>Simple</button>
          <button type="button" onClick={() => switchMode('advanced')} aria-pressed={mode === 'advanced'} className={`rounded px-3 py-1.5 text-xs font-medium ${mode === 'advanced' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}>Advanced</button>
        </div>
      </div>
      {modeSwitchMessage && (
        <p data-flow="mode-switch-error" role="alert" className="text-xs text-amber-700">{modeSwitchMessage}</p>
      )}

      {mode === 'simple' ? (
        <div data-flow="simple-mode" className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Query term</div>
          <SimpleLeafEditor
            leaf={simple.leaf}
            onChange={leaf => updateSimple({ leaf })}
          />
        </div>
      ) : (
        <div data-flow="advanced-mode" className="space-y-2">
          {isSimpleExpression(value.root) && (
            <button type="button" onClick={addAdvancedCondition} className="text-xs font-medium text-blue-600 hover:text-blue-800" aria-label="Add another root condition">+ Add root condition</button>
          )}
          <NodeEditor node={value.root} onChange={root => updateAdvanced({ ...value, root })} isRoot />
        </div>
      )}

      <div data-flow="query-preview" className="rounded-md border border-blue-100 bg-blue-50 p-3">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-800">Preview</div>
        <div className="break-words text-sm text-gray-800" aria-live="polite">{preview || 'Add a condition to preview the query.'}</div>
      </div>
      {validationMessage && <p data-flow="query-error" className="text-xs text-red-600">{validationMessage}</p>}
    </div>
  );
};
